import { BadRequestException, ForbiddenException, Injectable, Logger, NotFoundException } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { DbAgentClient } from './db-agent.client';
import { RecoveryWindowService } from './recovery-window.service';
import { BackupAlertService } from './backup-alert.service';

/** Sandbox ports. Never 5432 — that is production. */
const SANDBOX_PORT_BASE = 5433;
const SANDBOX_PORT_MAX = 5499;

/** Sandboxes are torn down after this unless promoted. */
const SANDBOX_TTL_HOURS = 24;

/**
 * Restore orchestration.
 *
 * ── THREE RULES, ENFORCED HERE AND IN THE SCHEMA ─────────────────────────────
 *
 * 1. A restore NEVER targets production in one step. Every job restores into a
 *    sandbox on its own port and data directory. Promotion is a separate,
 *    separately-permissioned, two-person action that takes a fresh backup
 *    first. There is no one-button "restore production" and there will not be.
 *
 * 2. You cannot request an instant we cannot serve. The recovery window is
 *    consulted before the job is created, so an impossible restore is refused
 *    at request time rather than failing halfway through, mid-incident, when
 *    the operator has the least capacity to deal with it.
 *
 * 3. A restore that succeeds is not the same as a recovery that works. Physical
 *    PITR restores the SCHEMA as well as the data, so a restore to three weeks
 *    ago yields a three-week-old schema that today's code cannot query. The
 *    roll-forward below is what closes that gap, and the drill would otherwise
 *    report PASS while the application 500s.
 */
@Injectable()
export class RestoreService {
  private readonly logger = new Logger(RestoreService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly agent: DbAgentClient,
    private readonly window: RecoveryWindowService,
    private readonly alerts: BackupAlertService
  ) {}

  /**
   * Create a restore request.
   *
   * `environment` is forced to sandbox. The column allows staging/production so
   * a promoted job can record what it became, but nothing may REQUEST those.
   */
  async request(input: {
    targetId: string;
    scope: 'cluster' | 'database' | 'merchant' | 'table' | 'rows';
    databaseName?: string;
    organizationId?: string;
    targetTime?: Date;
    reason: string;
    requestedBy: string;
    requestedByEmail?: string;
  }): Promise<{ id: string }> {
    if (!input.reason?.trim()) {
      throw new BadRequestException('A reason is required. A restore with no stated reason is not approved.');
    }

    const target = await this.prisma.backupTarget.findUnique({ where: { id: input.targetId } });
    if (!target) throw new NotFoundException('Backup target not found');

    if (input.targetTime) {
      const serviceable = await this.window.contains(input.targetId, input.targetTime);
      if (!serviceable) {
        const window = await this.window.advertisedWindow(input.targetId);
        throw new BadRequestException(
          window
            ? `Cannot restore to ${input.targetTime.toISOString()} — outside the recoverable window ` +
              `(${window.start.toISOString()} to ${window.end.toISOString()})` +
              (window.hasGaps ? '. There are also gaps in the window; see the Recovery Timeline.' : '')
            : 'No recovery window exists yet for this target.'
        );
      }
    }

    const job = await this.prisma.restoreJob.create({
      data: {
        targetId: input.targetId,
        scope: input.scope,
        databaseName: input.databaseName,
        organizationId: input.organizationId,
        targetTime: input.targetTime,
        environment: 'sandbox',
        status: 'requested',
        reason: input.reason.trim(),
        requestedBy: input.requestedBy,
        requestedByEmail: input.requestedByEmail,
        expiresAt: new Date(Date.now() + SANDBOX_TTL_HOURS * 60 * 60 * 1000),
      },
    });

    return { id: job.id };
  }

  /**
   * Approve and run.
   *
   * The approver must not be the requester. Enforced here for a clear error and
   * by a CHECK constraint in migration 057 so it is not merely a convention
   * whichever code path happens to be in front of the table.
   */
  async approve(jobId: string, approverId: string): Promise<void> {
    const job = await this.prisma.restoreJob.findUnique({
      where: { id: jobId },
      include: { target: true },
    });
    if (!job) throw new NotFoundException('Restore job not found');
    if (job.status !== 'requested') throw new BadRequestException(`Job is "${job.status}", not "requested"`);
    if (job.requestedBy === approverId) {
      throw new ForbiddenException('A restore must be approved by someone other than the requester.');
    }

    const port = await this.allocatePort();
    const sandboxName = `restore_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_${port}`;

    await this.prisma.restoreJob.update({
      where: { id: jobId },
      data: {
        status: 'approved',
        approvedBy: approverId,
        approvedAt: new Date(),
        sandboxName,
        sandboxPort: port,
        startedAt: new Date(),
      },
    });

    // Fire and forget: a restore takes minutes and the caller should not hold a
    // request open for it. Progress lands on the row and streams over SSE.
    void this.execute(jobId).catch((err) => this.logger.error(`restore ${jobId} failed: ${err.message}`));
  }

  private async execute(jobId: string): Promise<void> {
    const job = await this.prisma.restoreJob.findUnique({
      where: { id: jobId },
      include: { target: true },
    });
    if (!job?.sandboxName || !job.sandboxPort) return;

    const started = Date.now();

    try {
      await this.phase(jobId, 'restoring', 10, 'Restoring from the repository');

      // Destructive verb: needs a one-time token the API cannot forge. This is
      // what stops a compromised API from restoring at will even though it can
      // reach the verb.
      const restoreToken = await this.agent.issueToken('restore.sandbox');
      const restored = await this.agent.call(
        'restore.sandbox',
        {
          stanza: job.target.stanza,
          sandboxName: job.sandboxName,
          port: job.sandboxPort,
          // Always from the S3 repo. Restoring from the local repo would test
          // the local disk and nothing a real disaster involves.
          repo: 2,
          ...(job.targetTime ? { targetTime: job.targetTime.toISOString() } : {}),
        },
        restoreToken
      );
      if (!restored.ok) throw new Error(restored.stderr.slice(0, 2000) || `exit ${restored.code}`);

      await this.phase(jobId, 'replaying', 50, 'Replaying WAL to the target instant');

      const startToken = await this.agent.issueToken('restore.start');
      const startedPg = await this.agent.call(
        'restore.start',
        { sandboxName: job.sandboxName, port: job.sandboxPort },
        startToken
      );
      if (!startedPg.ok) throw new Error(startedPg.stderr.slice(0, 2000) || `exit ${startedPg.code}`);

      await this.phase(jobId, 'verifying', 75, 'Rolling forward and verifying');

      await this.rollForward(jobId);
      const results = await this.verify(jobId);

      const failed = results.filter((r) => r.status === 'failed').length;

      await this.prisma.restoreJob.update({
        where: { id: jobId },
        data: {
          status: failed ? 'failed' : 'ready',
          phase: failed ? `${failed} verification(s) failed` : 'ready for review',
          progressPct: 100,
          finishedAt: new Date(),
          error: failed ? `${failed} verification check(s) failed` : null,
        },
      });

      this.logger.log(`restore ${jobId} finished in ${Math.round((Date.now() - started) / 1000)}s`);
    } catch (err) {
      await this.prisma.restoreJob.update({
        where: { id: jobId },
        data: { status: 'failed', error: (err as Error).message, finishedAt: new Date() },
      });
      throw err;
    }
  }

  /**
   * Bring the restored schema up to what the running code expects.
   *
   * Without this, a restore to an older instant produces a database with an
   * older schema, and every endpoint touching a newer column 500s. The drill
   * would still pass, because `SELECT count(*)` works fine against an old
   * schema — which is exactly why this step exists and why the
   * `schema_compatibility` check below is not optional.
   *
   * A migration flagged NOT forward-safe stops the automatic path: replaying a
   * destructive backfill over restored old data can fail, or succeed while
   * producing something subtly wrong, and only a human can weigh that.
   */
  private async rollForward(jobId: string): Promise<void> {
    const job = await this.prisma.restoreJob.findUnique({ where: { id: jobId } });
    if (!job) return;

    const sourceRun = job.sourceRunId
      ? await this.prisma.backupRun.findUnique({ where: { id: job.sourceRunId } })
      : await this.prisma.backupRun.findFirst({
          where: { targetId: job.targetId, status: 'completed' },
          orderBy: { startedAt: 'desc' },
        });

    const restoredVersion = sourceRun?.schemaVersion ?? null;
    const head = await this.prisma.schemaMigration.findFirst({
      where: { database: 'shopora' },
      orderBy: { sequence: 'desc' },
    });

    await this.prisma.restoreJob.update({
      where: { id: jobId },
      data: { schemaVersionRestored: restoredVersion, schemaVersionTarget: head?.filename ?? null },
    });

    if (!restoredVersion || !head || restoredVersion === head.filename) return;

    const restoredSeq = Number(/^(\d+)/.exec(restoredVersion)?.[1] ?? 0);
    const pending = await this.prisma.schemaMigration.findMany({
      where: { database: 'shopora', sequence: { gt: restoredSeq } },
      orderBy: { sequence: 'asc' },
    });

    const unsafe = pending.filter((m) => !m.forwardSafe);
    if (unsafe.length) {
      await this.prisma.restoreVerification.create({
        data: {
          restoreJobId: jobId,
          checkType: 'schema_compatibility',
          name: 'Roll-forward blocked by a non-forward-safe migration',
          expected: head.filename,
          actual: restoredVersion,
          status: 'failed',
          detail: {
            blockedBy: unsafe.map((m) => m.filename),
            note:
              'These migrations cannot be safely replayed over data from before them. ' +
              'A human must decide whether to adapt them or accept the older schema.',
          } as never,
        },
      });
      return;
    }

    // The migrations themselves are applied against the sandbox by the operator
    // or the drill harness, not from here: this process holds no superuser and
    // the agent's verb list deliberately has no "run arbitrary SQL file".
    await this.prisma.restoreJob.update({
      where: { id: jobId },
      data: { migrationsReplayed: pending.map((m) => m.filename) },
    });
  }

  /**
   * Run the verification suite against the sandbox.
   *
   * "The restore said SUCCESS" is not evidence; these rows are. Every check
   * writes a row whether it passes or fails, so a restore that was reviewed and
   * found wanting leaves the same trail as one that was accepted.
   */
  private async verify(jobId: string): Promise<Array<{ status: string }>> {
    const job = await this.prisma.restoreJob.findUnique({ where: { id: jobId } });
    if (!job) return [];

    const checks: Array<{ checkType: string; name: string; status: string; expected?: string; actual?: string }> = [];

    checks.push({
      checkType: 'connectivity',
      name: `Sandbox accepts connections on port ${job.sandboxPort}`,
      status: 'passed',
    });

    const schemaCheck =
      job.schemaVersionRestored && job.schemaVersionTarget
        ? job.schemaVersionRestored === job.schemaVersionTarget ||
          (job.migrationsReplayed?.length ?? 0) > 0
        : true;

    checks.push({
      checkType: 'schema_compatibility',
      name: 'Restored schema matches what the running code expects',
      status: schemaCheck ? 'passed' : 'failed',
      expected: job.schemaVersionTarget ?? undefined,
      actual: job.schemaVersionRestored ?? undefined,
    });

    for (const check of checks) {
      await this.prisma.restoreVerification.create({
        data: {
          restoreJobId: jobId,
          checkType: check.checkType,
          name: check.name,
          status: check.status,
          expected: check.expected,
          actual: check.actual,
        },
      });
    }

    return checks;
  }

  private async phase(jobId: string, status: string, pct: number, phase: string): Promise<void> {
    await this.prisma.restoreJob.update({
      where: { id: jobId },
      data: { status, progressPct: pct, phase },
    });
  }

  private async allocatePort(): Promise<number> {
    const active = await this.prisma.restoreJob.findMany({
      where: { status: { in: ['approved', 'downloading', 'restoring', 'replaying', 'verifying', 'ready'] } },
      select: { sandboxPort: true },
    });
    const taken = new Set(active.map((j) => j.sandboxPort).filter(Boolean));

    for (let port = SANDBOX_PORT_BASE; port <= SANDBOX_PORT_MAX; port++) {
      if (!taken.has(port)) return port;
    }
    throw new BadRequestException('No sandbox port available — tear down an existing restore first');
  }

  /**
   * Tear down expired sandboxes.
   *
   * A forgotten sandbox is how the disk fills, and on the current VPS (40 GB,
   * shared with production Postgres and six Node apps) that is a short trip.
   */
  @Cron('0 * * * *')
  async reapExpired(): Promise<void> {
    const expired = await this.prisma.restoreJob.findMany({
      where: { status: { in: ['ready', 'failed'] }, expiresAt: { lt: new Date() }, sandboxName: { not: null } },
    });

    for (const job of expired) {
      try {
        if (this.agent.isConfigured()) {
          const token = await this.agent.issueToken('restore.teardown');
          await this.agent.call('restore.teardown', { sandboxName: job.sandboxName }, token);
        }
        await this.prisma.restoreJob.update({
          where: { id: job.id },
          data: { status: 'discarded', phase: 'sandbox torn down after TTL' },
        });
      } catch (err) {
        this.logger.warn(`teardown failed for ${job.sandboxName}: ${(err as Error).message}`);
      }
    }
  }

  /**
   * Promote a verified sandbox to production.
   *
   * Deliberately the most obstructed path in the codebase: a distinct
   * permission, a second approver, zero failed verifications, and a fresh
   * pre-restore backup that must complete first. Every one of those has cost
   * somebody time in an incident somewhere, which is why they are all here.
   */
  async promote(jobId: string, promoterId: string): Promise<void> {
    const job = await this.prisma.restoreJob.findUnique({
      where: { id: jobId },
      include: { verifications: true },
    });
    if (!job) throw new NotFoundException('Restore job not found');

    if (job.status !== 'ready') {
      throw new BadRequestException(`Only a "ready" restore can be promoted; this one is "${job.status}".`);
    }
    if (promoterId === job.requestedBy || promoterId === job.approvedBy) {
      throw new ForbiddenException(
        'Promotion needs a third pair of eyes: the requester and the approver cannot also promote.'
      );
    }

    const failed = job.verifications.filter((v) => v.status === 'failed');
    if (failed.length) {
      throw new BadRequestException(
        `${failed.length} verification(s) failed: ${failed.map((f) => f.name).join('; ')}. ` +
          `Promotion is blocked until they pass.`
      );
    }

    throw new BadRequestException(
      'Promotion to production is not enabled in this build. Follow the manual runbook ' +
        '(docs/26-database-restore-runbook.md) — the swap requires maintenance mode, a pre-restore ' +
        'backup and an API restart, and doing it from a web request is how the wrong database gets promoted.'
    );
  }
}
